import type { Building } from "building-types/building/buildingTypes";
import {
  isBuildingPlanView,
  type BuildingGeometryEmitter,
  type BuildingGeometryOptions,
  BuildingGeometryMode,
} from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import { FaceCategory } from "building-types/materials/faceCategories";
import { MS_Color_PlanFloor__Default } from "common/materials/library/designMaterialSrcDefaults";
import { isInteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { faceNew } from "geom/brep";
import { DIST_Z_FIGHT } from "geom/core";
import { planeCopyMoveAlongNormal } from "geom/plane";
import { UVGeneration } from "geom/uv";
import { buildExteriorLayers, buildInteriorLayers } from "./buildingGeometryTools";

type BuildingForFloorGeometries = Pick<Building, "rooms" | "floorSystems">;

export function getFloorFinishGeometries(
  building: BuildingForFloorGeometries,
  options: BuildingGeometryOptions,
  emitter: BuildingGeometryEmitter,
): void {
  const { rooms, floorSystems } = building;
  const usePlanMat = isBuildingPlanView(options);
  const enableLegacyDepthOffsets = options.mode !== BuildingGeometryMode.EXPORT;

  for (const roomId in rooms) {
    const { attributes, level, pickHandle } = rooms[roomId];
    const { roomEnclosure } = attributes;
    const levelId = level.id;

    const faceCategory = isWalledRoomEnclosure[roomEnclosure] ? FaceCategory.FLOOR : FaceCategory.PORCH_EXTERIOR;
    const threeLayers = isInteriorRoomEnclosure[roomEnclosure] ? buildInteriorLayers() : buildExteriorLayers();
    // A doc with no walls can leave rooms without a generated floor system.
    const floorFinishes = floorSystems[levelId]?.roomFloorFinishes[roomId] ?? [];

    const entityHandleForSelectingReportingUnit: DocEntityHandle | undefined =
      roomEntityHandleForSelectingReportingUnit(rooms[roomId]);

    for (const finish of floorFinishes) {
      const { finishFace, materialSrc, materialSlot } = finish;

      const finalMaterialSrc = !usePlanMat ? materialSrc : MS_Color_PlanFloor__Default;
      const geom = enableLegacyDepthOffsets
        ? finishFace
        : faceNew(finishFace.loops, planeCopyMoveAlongNormal(finishFace.plane, -DIST_Z_FIGHT), finishFace.tags);

      emitter.emit({
        geom,
        faceCategory,
        threeLayers,
        entityId: roomId,
        entityHandleForSelectingReportingUnit,
        levelId,
        pickHandle,
        materialSlot,
        materialSrc: finalMaterialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
      });
    }
  }
}
